import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import products from "../data/products";

const HERO_TRUST = [
  "Free onboarding & data migration",
  "No hidden fees",
  "Clear quote within 1 business day",
];

const INCLUDED_IN_ALL = [
  { icon: "🚀", title: "Guided onboarding", desc: "Setup, data migration, and configuration handled with you." },
  { icon: "🎓", title: "Team training", desc: "Role-based training so your staff are productive from day one." },
  { icon: "🔄", title: "Product updates", desc: "Continuous improvements and new features at no extra cost." },
  { icon: "🔐", title: "Security & audit logs", desc: "Role-based permissions and full audit trails on every plan." },
  { icon: "📱", title: "Mobile access", desc: "Native and mobile-first workflows included, never an add-on." },
  { icon: "🔗", title: "Integrations & API", desc: "Connect the tools you already use, with an open API." },
];

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
    />
  </svg>
);

const PHILOSOPHY = [
  {
    icon: "🎯",
    title: "Tailored to your business",
    desc: "Pricing scales with team size, modules, and deployment — so you only pay for what you actually use.",
  },
  {
    icon: "📦",
    title: "Modular by design",
    desc: "Start with what you need today. Switch on additional modules whenever your team is ready.",
  },
  {
    icon: "🧪",
    title: "Free pilot, when applicable",
    desc: "For qualified businesses we set up a sandbox so you can try Byteflow before committing.",
  },
  {
    icon: "🤝",
    title: "No surprises",
    desc: "Onboarding, training, and product updates are included in every plan. No hidden fees.",
  },
];

const FAQS = [
  {
    q: "How is Byteflow priced?",
    a: "Pricing is based on the product you choose, the modules you turn on, your team size, and whether you need cloud or on-premise deployment. We quote you a clear monthly or annual price after a short discovery call.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — for qualified businesses we provision a sandbox with sample data so your team can explore the product before you commit. Reach out and we’ll get you set up.",
  },
  {
    q: "Are setup, training, and support included?",
    a: "Every plan includes onboarding, training for your team, and ongoing product updates. Higher tiers add priority support, dedicated account managers, and 24/7 coverage.",
  },
  {
    q: "Can I switch products or plans later?",
    a: "Yes. You can switch plans up or down as your business changes, and you can add additional Byteflow products at any time without redoing your setup.",
  },
  {
    q: "Do you offer annual discounts or custom pricing?",
    a: "Yes — annual billing comes with a meaningful discount, and we can build custom pricing for multi-product, multi-location, or enterprise deployments.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Pricing = () => {
  const [activeSlug, setActiveSlug] = useState(products[0].slug);
  const active = products.find((p) => p.slug === activeSlug);

  return (
    <>
      <section className="page-hero">
        <motion.div
          className="page-hero__inner"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <p className="eyebrow">Pricing</p>
          <h1>Pricing built around your business.</h1>
          <p className="page-hero__lead">
            We tailor pricing to your team size, modules, and deployment.
            Tell us a little about your business and we’ll send a clear quote
            within one business day.
          </p>
          <div className="hero__ctas" style={{ justifyContent: "center" }}>
            <Link to="/contact" className="btn-primary">Get a quote</Link>
            <a href="#plans" className="btn-secondary">See plan tiers</a>
          </div>
          <ul className="pricing-hero__trust">
            {HERO_TRUST.map((item) => (
              <li key={item}>
                <CheckIcon /> {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      <section className="section">
        <motion.div
          className="section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">How we price</p>
          <h2>Fair, flexible, and built for SMBs.</h2>
        </motion.div>

        <div className="philosophy-grid">
          {PHILOSOPHY.map((item, i) => (
            <motion.div
              key={item.title}
              className="philosophy-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
            >
              <div className="philosophy-card__icon" aria-hidden>{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="plans" className="section section--muted">
        <motion.div
          className="section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">Plan tiers</p>
          <h2>Choose a product. We’ll quote the right plan.</h2>
          <p className="section__lead">
            Every Byteflow product comes in three tiers, scaled to your
            business size and complexity.
          </p>
        </motion.div>

        <div
          className="pricing-tabs"
          role="tablist"
          aria-label="Choose a product"
        >
          {products.map((p) => (
            <button
              key={p.slug}
              role="tab"
              aria-selected={p.slug === activeSlug}
              type="button"
              className={"pricing-tab" + (p.slug === activeSlug ? " is-active" : "")}
              onClick={() => setActiveSlug(p.slug)}
              style={{ "--product-accent": p.accent }}
            >
              <span aria-hidden>{p.icon}</span>
              <span>{p.shortName}</span>
              {p.flagship && <span className="pricing-tab__badge">Flagship</span>}
            </button>
          ))}
        </div>

        <motion.div
          key={active.slug}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="pricing-summary">
            <h3>{active.name}</h3>
            <p>{active.tagline}</p>
          </div>

          <div className="pricing-grid">
            {active.pricing.map((plan, i) => (
              <div
                key={plan.name}
                className={"pricing-card" + (plan.featured ? " is-featured" : "")}
                style={{ "--product-accent": active.accent }}
              >
                {plan.featured && <div className="pricing-card__badge">Most popular</div>}
                <div className="pricing-card__head">
                  <span className="pricing-card__tier">
                    {i === 0 ? "Starter tier" : i === 1 ? "Growth tier" : "Enterprise tier"}
                  </span>
                  <h3>{plan.name}</h3>
                  {plan.audience && (
                    <p className="pricing-card__audience">{plan.audience}</p>
                  )}
                </div>
                <div className="pricing-card__price">
                  <span className="pricing-card__price-value">Custom</span>
                  <span className="pricing-card__price-note">
                    tailored to your team &amp; modules
                  </span>
                </div>
                <Link
                  to="/contact"
                  className={
                    (plan.featured ? "btn-primary" : "btn-secondary") +
                    " pricing-card__cta"
                  }
                >
                  {plan.featured ? "Get a quote" : "Talk to sales"}
                </Link>
                <div className="pricing-card__included-label">What's included</div>
                <ul>
                  {plan.features.map((f) => (
                    <li key={f}>
                      <CheckIcon />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="pricing-footer">
            <Link to={`/products/${active.slug}`} className="btn-secondary">
              See {active.shortName} features →
            </Link>
          </div>
        </motion.div>
      </section>

      <section className="section">
        <motion.div
          className="section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">Every plan includes</p>
          <h2>The essentials, never an upsell.</h2>
          <p className="section__lead">
            No matter which product or tier you choose, these come standard.
          </p>
        </motion.div>

        <div className="included-grid">
          {INCLUDED_IN_ALL.map((item, i) => (
            <motion.div
              key={item.title}
              className="included-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <span className="included-card__icon" aria-hidden>{item.icon}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section section--muted">
        <motion.div
          className="section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">FAQ</p>
          <h2>Pricing, in plain English.</h2>
        </motion.div>

        <div className="faq-list">
          {FAQS.map((item) => (
            <details key={item.q} className="faq-item">
              <summary>
                <span>{item.q}</span>
                <span className="faq-item__chevron" aria-hidden>+</span>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="cta-strip">
        <div className="cta-strip__inner">
          <div>
            <h2>Ready for a tailored quote?</h2>
            <p>One 30-minute call. A clear price within a business day. No commitment.</p>
          </div>
          <Link to="/contact" className="btn-primary btn-primary--inverse">
            Get a quote
          </Link>
        </div>
      </section>
    </>
  );
};

export default Pricing;
